// Originally auto-generated from the Org_KPI_Master CSV (Downloads); statuses
// are now hand-audited against this repo's actual schema/ and ingestion code,
// not the CSV's Have-Data column, which several times overstated readiness.
// The org's KPI list is re-grouped by BUSINESS THEME (cuts across the source
// spreadsheet's team sections). Each KPI keeps its owning `team` and a `status`:
// live = on this dashboard; dataAvailable = raw data is already ingested into
// Canon, no chart built yet; blocked = the source system is identified but nothing in
// this repo ingests it yet (a new pipeline is needed, not just a chart);
// scoping = the metric's definition/formula needs a decision before any chart
// or pipeline work can start; none = no source has even been identified. KPIs
// are sorted within a theme by readiness (live first).

export type KpiStatus = 'live' | 'dataAvailable' | 'blocked' | 'scoping' | 'none';

export const hasData = (status: KpiStatus): boolean =>
  status === 'live' || status === 'dataAvailable';

export interface CatalogKpi {
  name: string;
  sources: string;
  origin: string;
  haveData: string;
  loe: string;
  status: KpiStatus;
  team: string;
  anchor?: string;
}

export interface KpiTheme {
  theme: string;
  kpis: CatalogKpi[];
}

export const kpiThemes: KpiTheme[] = [
  {
    theme: 'Revenue',
    kpis: [
      {
        name: 'Revenue (by business line / service)',
        sources: 'Wealth, Marketing, Product',
        origin: 'Stripe',
        haveData: 'Yes (need insurance)',
        loe: '4',
        status: 'live',
        team: 'Shared',
        anchor: 'chart-revenue-line',
      },
      {
        name: 'Revenue mix concentration by product',
        sources: 'Marketing/Sales, Product',
        origin: 'Stripe',
        haveData: 'Yes',
        loe: '4',
        status: 'live',
        team: 'Product',
        anchor: 'chart-mix',
      },
      {
        name: 'Revenue per client',
        sources: 'Wealth, Marketing, Product',
        origin: 'Stripe, Anchorage',
        haveData: 'Yes (need insurance)',
        loe: '6',
        status: 'live',
        team: 'Shared',
        anchor: 'kpi-rev-per-client',
      },
      {
        name: 'MRR/ARR by Product Line',
        sources: 'Marketing/Sales, Product',
        origin: 'Stripe, Anchorage',
        haveData: 'Yes',
        loe: '2',
        status: 'live',
        team: 'Product',
        anchor: 'chart-mrr',
      },
      {
        name: 'Paid transactions vs failed payments vs refunds',
        sources: 'Ops',
        origin: 'Stripe',
        haveData: 'Yes',
        loe: '2',
        status: 'live',
        team: 'Ops',
        anchor: 'chart-payments',
      },
      {
        name: 'LLC - new vs renewals',
        sources: 'Ops',
        origin: 'Stripe, DAG App',
        haveData: 'Yes',
        loe: '2',
        status: 'live',
        team: 'Ops',
        anchor: 'chart-llc-sales',
      },
      {
        name: 'Expansion revenue rate',
        sources: 'Marketing/Sales, Product',
        origin: 'Stripe',
        haveData: 'Yes',
        loe: '4',
        status: 'dataAvailable',
        team: 'Product',
      },
      {
        name: 'Gross margin by product (?)',
        sources: 'Marketing/Sales, Product',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Product',
      },
      {
        name: 'Revenue at risk by product',
        sources: 'Marketing/Sales, Product',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Product',
      },
    ],
  },
  {
    theme: 'Growth & Acquisition',
    kpis: [
      {
        name: 'Client Growth (Private Clients vs. Wealth)',
        sources: 'Private Client, Wealth, Marketing',
        origin: 'DAG App, DWP Onboarding',
        haveData: 'Yes',
        loe: '',
        status: 'live',
        team: 'Shared',
        anchor: 'stat-client-count',
      },
      {
        name: 'New Users by First Product Purchase',
        sources: '',
        origin: 'Stripe',
        haveData: 'Yes (need insurance)',
        loe: '',
        status: 'scoping',
        team: 'Shared',
      },
      {
        name: 'Organic Customers',
        sources: 'Marketing',
        origin: 'GA4',
        haveData: 'No — GA4 export is not enabled',
        loe: '4',
        status: 'blocked',
        team: 'Marketing',
      },
      {
        name: 'Self-serve activation rate',
        sources: 'Marketing/Sales, Product',
        origin: 'DWP App',
        haveData:
          'No — only 10 admin columns of InvestmentAccounts are ingested, no activation events',
        loe: '4',
        status: 'blocked',
        team: 'Product',
      },
      {
        name: 'Time to activation by product x segment',
        sources: 'Marketing/Sales, Product',
        origin: 'Formidable, Stripe, Anchorage',
        haveData: 'No — Formidable is not ingested',
        loe: '4',
        status: 'blocked',
        team: 'Product',
      },
      {
        name: 'Total Customers',
        sources: 'Marketing',
        origin: 'Stripe',
        haveData: 'Yes',
        loe: '',
        status: 'dataAvailable',
        team: 'Marketing',
      },
      {
        name: 'ARO Ranking Score',
        sources: 'Marketing',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Marketing',
      },
      {
        name: 'CAC by Segment',
        sources: 'Marketing',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Marketing',
      },
      {
        name: 'Cost per Qualified Lead',
        sources: 'Marketing',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Marketing',
      },
      {
        name: 'LTV:CAC Ratio by Segment',
        sources: 'Marketing',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Marketing',
      },
      {
        name: 'Lead source / channel attribution',
        sources: 'Wealth, Marketing, Product',
        origin: '',
        haveData: 'No',
        loe: 'TBD - 5',
        status: 'none',
        team: 'Shared',
      },
      {
        name: 'Lead-to-Qualified Conversion Rate',
        sources: 'Marketing',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Marketing',
      },
      {
        name: 'Onboarding funnel conversion rate by segment',
        sources: 'Marketing/Sales, Product',
        origin: '',
        haveData: 'Not yet. Wait for new site',
        loe: '',
        status: 'none',
        team: 'Product',
      },
      {
        name: 'Qualified Lead Volume by Channel',
        sources: 'Marketing',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Marketing',
      },
      {
        name: 'ROAS by Channel',
        sources: 'Marketing',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Marketing',
      },
    ],
  },
  {
    theme: 'Retention & Expansion',
    kpis: [
      {
        name: 'Revenue Retention',
        sources: '',
        origin: 'Stripe',
        haveData: 'Yes',
        loe: '',
        status: 'live',
        team: 'Shared',
        anchor: 'stat-revenue-retention',
      },
      {
        name: 'Asset Retention',
        sources: '',
        origin: 'Anchorage',
        haveData: 'Yes',
        loe: '',
        status: 'live',
        team: 'Shared',
        anchor: 'stat-asset-retention',
      },
      {
        name: 'Product Attachment Rate',
        sources: 'Marketing/Sales, Product',
        origin: 'Stripe',
        haveData: 'Blocked on cross-entity user reconciliation',
        loe: '3',
        status: 'scoping',
        team: 'Product',
      },
      {
        name: 'Services per Client',
        sources: 'Marketing',
        origin: 'Stripe',
        haveData: 'Blocked on cross-entity user reconciliation',
        loe: '4',
        status: 'scoping',
        team: 'Marketing',
      },
      {
        name: 'Time to second product',
        sources: 'Marketing/Sales, Product',
        origin: 'Stripe',
        haveData: 'Blocked on cross-entity user reconciliation',
        loe: '3',
        status: 'scoping',
        team: 'Product',
      },
      {
        name: 'Account opening date / longevity of active client account',
        sources: 'Wealth, Marketing, Product',
        origin: 'Anchorage',
        haveData: '?',
        loe: '',
        status: 'scoping',
        team: 'Product',
      },
      {
        name: 'Cross Purchase Rate',
        sources: 'Marketing',
        origin: '',
        haveData: 'For some, yes',
        loe: '',
        status: 'scoping',
        team: 'Marketing',
      },
      {
        name: 'Cross-sell conversion rate by segment',
        sources: 'Marketing/Sales, Product',
        origin: 'Stripe',
        haveData: 'What are the Segments?',
        loe: '4',
        status: 'scoping',
        team: 'Product',
      },
      {
        name: 'In-app cross-sell click-through',
        sources: 'Marketing/Sales, Product',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Product',
      },
      {
        name: 'Lifetime Value (LTV)',
        sources: 'Marketing',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Marketing',
      },
      {
        name: 'Private Client upsell rate (?)',
        sources: 'Marketing/Sales, Product',
        origin: '',
        haveData: 'No, could be a data export',
        loe: '',
        status: 'none',
        team: 'Product',
      },
    ],
  },
  {
    theme: 'Wealth & AUM',
    kpis: [
      {
        name: 'Average account size',
        sources: 'Wealth',
        origin: 'Anchorage',
        haveData: 'Yes',
        loe: '3',
        status: 'live',
        team: 'Wealth',
        anchor: 'chart-wealth-avg-account-size',
      },
      {
        name: 'Enterprise AUM (aggregate & per RIA)',
        sources: 'Wealth',
        origin: 'Anchorage',
        haveData:
          'Aggregate done (daily total, custodied vs. grand total); per-RIA breakdown not built',
        loe: '2',
        status: 'live',
        team: 'Wealth',
        anchor: 'chart-wealth-aum-trend',
      },
      {
        name: 'AUM by Custodian',
        sources: 'Wealth',
        origin: 'Anchorage',
        haveData: 'Yes',
        loe: '',
        status: 'live',
        team: 'Wealth',
        anchor: 'chart-wealth-aum-by-custodian',
      },
      {
        name: 'AUM by Asset',
        sources: 'Wealth',
        origin: 'Anchorage',
        haveData:
          'Yes — proxy strategy positions (e.g. XRP_TACTIC) collapsed into their base asset',
        loe: '',
        status: 'live',
        team: 'Wealth',
        anchor: 'chart-wealth-aum-by-asset',
      },
      {
        name: 'Enterprise revenue / AUM fees (aggregate & per RIA)',
        sources: 'Wealth',
        origin: 'Anchorage',
        haveData:
          'No — AUM-fee billing is a separate system from the ingested transaction/balance data, likely not API-accessible',
        loe: '2',
        status: 'blocked',
        team: 'Wealth',
      },
      {
        name: 'Gross inflows and outflows by token/product',
        sources: 'Wealth',
        origin: 'Anchorage',
        haveData: 'Yes',
        loe: '6',
        status: 'live',
        team: 'Wealth',
        anchor: 'chart-flows-by-token',
      },
      {
        name: 'Model adoption (AUM & # clients per model)',
        sources: 'Wealth',
        origin: 'Anchorage',
        haveData:
          'Unclear — no "model" assignment field is ingested today, only tokens/balances/transactions',
        loe: '3',
        status: 'scoping',
        team: 'Wealth',
      },
      {
        name: 'Net New AUM',
        sources: 'Wealth, Marketing, Product',
        origin: 'Anchorage',
        haveData:
          'Yes — net flow is already computed from ingested transactions, no dedicated chart yet',
        loe: '3',
        status: 'dataAvailable',
        team: 'Shared',
      },
      {
        name: 'Net inflows/outflows per advisor',
        sources: 'Wealth',
        origin: 'Anchorage',
        haveData: 'Yes',
        loe: '4',
        status: 'live',
        team: 'Wealth',
        anchor: 'chart-flows-by-advisor',
      },
      {
        name: 'New / funded client accounts',
        sources: 'Wealth, Marketing, Product',
        origin: 'Anchorage',
        haveData: 'Yes (need insurance)',
        loe: '3',
        status: 'live',
        team: 'Shared',
        anchor: 'chart-funded-accounts',
      },
      {
        name: 'New RIAs onboarded',
        sources: 'Wealth',
        origin: 'Subadvisor',
        haveData: 'Yes',
        loe: '2',
        status: 'live',
        team: 'Wealth',
        anchor: 'chart-ria-onboarded',
      },
      {
        name: 'Performance by strategy (actual vs target)',
        sources: 'Wealth',
        origin: '',
        haveData: 'No — no source identified',
        loe: '',
        status: 'none',
        team: 'Wealth',
      },
      {
        name: 'Benchmark-relative returns',
        sources: 'Wealth',
        origin: '',
        haveData: 'Need Discussion',
        loe: '',
        status: 'scoping',
        team: 'Wealth',
      },
      {
        name: 'Client meetings',
        sources: 'Wealth',
        origin: 'Salesforce',
        haveData: 'Yes',
        loe: '',
        status: 'live',
        team: 'Wealth',
        anchor: 'chart-meetings-trend',
      },
      {
        name: 'Max drawdown',
        sources: 'Wealth',
        origin: '',
        haveData: 'Need Discussion',
        loe: '',
        status: 'scoping',
        team: 'Wealth',
      },
      {
        name: 'Model performance',
        sources: 'Wealth',
        origin: '',
        haveData: 'Need Discussion',
        loe: '',
        status: 'scoping',
        team: 'Wealth',
      },
      {
        name: 'Strategy capacity/utilization',
        sources: 'Wealth',
        origin: '',
        haveData: 'Need Discussion',
        loe: '',
        status: 'scoping',
        team: 'Wealth',
      },
    ],
  },
  {
    theme: 'Operations & Service',
    kpis: [
      {
        name: 'Ticket age (oldest / 90th pct / median)',
        sources: 'Ops',
        origin: 'Front',
        haveData: 'Yes',
        loe: '',
        status: 'live',
        team: 'Ops',
        anchor: 'stat-ticket-age',
      },
      {
        name: 'Avg revenue of ticket submitters (rolling 30d)',
        sources: 'Ops',
        origin: 'Front, Stripe, WooCommerce',
        haveData: 'Yes',
        loe: '',
        status: 'live',
        team: 'Ops',
        anchor: 'stat-ticket-submitter-revenue',
      },
      {
        name: 'Ticket counts (rolling 7 / 30 / 90 day)',
        sources: 'Ops',
        origin: 'Front',
        haveData: 'Yes',
        loe: '',
        status: 'live',
        team: 'Ops',
        anchor: 'stat-ticket-counts',
      },
      {
        name: 'Customer Sentiment (phone vs email)',
        sources: 'Ops',
        origin: '',
        haveData: 'No?',
        loe: '',
        status: 'none',
        team: 'Ops',
      },
      {
        name: 'New estate plans paid vs pending by type',
        sources: 'Ops',
        origin: 'Spreadsheet',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Ops',
      },
    ],
  },
  {
    theme: 'Risk & Compliance',
    kpis: [
      {
        name: 'Compliance Program Effectiveness & Testing',
        sources: 'Compliance',
        origin: 'Greenboard',
        haveData: 'No — Greenboard is not ingested',
        loe: '1',
        status: 'blocked',
        team: 'Compliance',
      },
      {
        name: 'Digital Asset Specific Controls',
        sources: 'Compliance',
        origin: '',
        haveData: 'No — no source identified',
        loe: '1',
        status: 'none',
        team: 'Compliance',
      },
      {
        name: 'Marketing materials governance / pre-clearance',
        sources: 'Marketing, Compliance',
        origin: 'Greenboard',
        haveData: 'No — Greenboard is not ingested',
        loe: 'Not sure how this could be measured.',
        status: 'blocked',
        team: 'Shared',
      },
      {
        name: 'Training, Culture, Code of Ethics',
        sources: 'Compliance',
        origin: 'Greenboard',
        haveData: 'No — Greenboard is not ingested',
        loe: '1',
        status: 'blocked',
        team: 'Compliance',
      },
      {
        name: 'Client Protection and Suitability',
        sources: 'Compliance',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Compliance',
      },
      {
        name: 'Operational Efficiency & Quantified Savings',
        sources: 'Compliance',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Compliance',
      },
    ],
  },
  {
    theme: 'Insurance',
    kpis: [
      {
        name: 'Appointments completed',
        sources: 'Insurance',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Insurance',
      },
      {
        name: 'Appointments set',
        sources: 'Insurance',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Insurance',
      },
      {
        name: 'Apps lost + comm premium',
        sources: 'Insurance',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Insurance',
      },
      {
        name: 'Commissionable premium submitted',
        sources: 'Insurance',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Insurance',
      },
      {
        name: 'Number of apps submitted',
        sources: 'Insurance',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Insurance',
      },
      {
        name: 'Policies issued + commissionable premium',
        sources: 'Insurance',
        origin: '',
        haveData: 'No',
        loe: '',
        status: 'none',
        team: 'Insurance',
      },
    ],
  },
];
